import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Grid,
} from "@mui/material";
import {
  EmojiEventsOutlined as TrophyIcon,
  NavigateNext as NavigateNextIcon,
  AutoAwesome as SparkleIcon,
  VerifiedUserOutlined as FairIcon,
  ReceiptLongOutlined as LedgerIcon,
  MonetizationOnOutlined as MoneyIcon,
  GroupOutlined as WinnersIcon,
  ShieldOutlined as ShieldIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { Path, DEFAULT_CURRENCY } from "../../../../../static/Constants";
import { CommonChip } from "../../../../Components/ui";

const ResultsHeroBanner = ({
  totalCompleted = 0,
  totalPrizeDistributed = "",
  uniqueWinnersCount = 0,
  currency = DEFAULT_CURRENCY,
}) => {
  console.log({totalPrizeDistributed})
  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2.5 }}
      >
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              sx={{ fontSize: 16, color: colors.slate[400] }}
            />
          }
          aria-label="breadcrumb"
          sx={{ fontSize: "0.875rem" }}
        >
          <Link
            to={Path.root}
            style={{
              textDecoration: "none",
              color: colors.slate[500],
              fontWeight: 500,
            }}
          >
            Home
          </Link>
          <Link
            to={Path.allLotteries}
            style={{
              textDecoration: "none",
              color: colors.slate[500],
              fontWeight: 500,
            }}
          >
            All Lotteries
          </Link>
          <Typography
            sx={{
              color: colors.primary.main,
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            Lottery Results
          </Typography>
        </Breadcrumbs>
      </Stack>

      {/* Hero Banner Box */}
      <Box className="results-hero-banner" sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)",
                }}
              >
                <TrophyIcon sx={{ fontSize: 26 }} />
              </Box>
              <div>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  VERIFIED ON-CHAIN LEDGER
                </Typography>
                <Typography variant="h4" className="results-hero-title">
                  Lottery Draw Results
                </Typography>
              </div>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                maxWidth: 540,
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Explore immutable draw results, verifiable winning tickets, and decentralized payouts executed automatically by smart contracts and Chainlink VRF.
            </Typography>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
              <CommonChip
                icon={<FairIcon sx={{ fontSize: 16 }} />}
                label="Provably Fair VRF"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<MoneyIcon sx={{ fontSize: 15 }} />}
                label="Trustless Payouts"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<SparkleIcon sx={{ fontSize: 15 }} />}
                label="Public Blockchain Records"
                variant="neutral"
                size="small"
              />
            </Stack>
          </Grid>

          {/* Quick Metrics Cards */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box className="results-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <LedgerIcon sx={{ fontSize: 18, color: colors.primary.main }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Completed Draws
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.slate[900] }}
                  >
                    {totalCompleted}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.emerald[600], fontWeight: 600 }}
                  >
                    ● On-chain verified
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="results-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <MoneyIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Rewards Distributed
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.emerald[700] }}
                  >
                    {totalPrizeDistributed} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Total prize value
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="results-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <WinnersIcon sx={{ fontSize: 18, color: colors.purple[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Unique Winners
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: colors.slate[900] }}
                  >
                    {uniqueWinnersCount} Wallets
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Rewarded players
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="results-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <ShieldIcon sx={{ fontSize: 18, color: colors.amber[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Decentralized
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: colors.slate[900] }}
                  >
                    100% Provable
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Tamper-proof draws
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

ResultsHeroBanner.propTypes = {
  totalCompleted: PropTypes.number,
  totalPrizeDistributed: PropTypes.string,
  uniqueWinnersCount: PropTypes.number,
  currency: PropTypes.string,
};

export default ResultsHeroBanner;
